/**
 * EvalResultsPageShell
 *
 * Phase 0 — foundation shell for the redesigned evaluation results page.
 * This component's ONLY job in Phase 0 is to set the `data-results-page-theme`
 * attribute on its root div, which activates the scoped CSS token block defined
 * in index.css under [data-results-page-theme="report"].
 *
 * PHASE 0 CONTRACT:
 *  • Renders `data-results-page-theme="report"` on the wrapper div.
 *  • Passes children through unchanged — no layout, no styling applied yet.
 *  • The live EvaluationResults.tsx page is NOT wrapped in this shell yet;
 *    that happens in Phase 1 once redesigned components consume the tokens.
 *    Adding the shell to the live page today would be visually inert (no
 *    existing component references --results-* vars), but wiring is deferred
 *    to Phase 1 to keep this PR zero-risk.
 *
 * PHASE 1+:
 *  • Redesigned components receive layout props (maxWidth, padding) via this
 *    shell rather than hard-coding layout in each component.
 *  • The shell may add a scroll-restoration effect and section-spy context.
 *
 * TOKEN CONSUMPTION:
 *  All components rendered inside this shell should consume only --results-*
 *  and --font-results-* CSS variables.  See RESULTS_THEME_CONVENTION.md.
 */

import React from 'react';
import type { ReactNode } from 'react';

interface EvalResultsPageShellProps {
  /** Page content — all redesigned eval-results components. */
  children: ReactNode;
  /**
   * Optional extra class names applied to the root div.
   * Use sparingly — prefer token-based styling in child components.
   */
  className?: string;
}

/**
 * Thin wrapper that activates the results-page scoped CSS token scope.
 *
 * Usage (Phase 1+):
 * ```tsx
 * <EvalResultsPageShell>
 *   <HeroScoreSection score={overall_score} />
 *   <CoachingNarrativeSection ... />
 * </EvalResultsPageShell>
 * ```
 */
export default function EvalResultsPageShell({
  children,
  className,
}: EvalResultsPageShellProps): React.JSX.Element {
  return (
    <div
      data-results-page-theme="report"
      className={className}
    >
      {children}
    </div>
  );
}
